import './MultiSelectBox.scss';
import classNames from 'classnames';
import cls from 'classnames';
import { useTrans } from 'core/hooks';
import { IFilterOption } from 'core/redux/features/shared/types';
import { selectDeviceType } from 'core/redux/reducers';
import { remove } from 'core/utils/object-prop';
import {
  style as addStyle,
  querySelectorAll as qsa,
  scrollTop,
} from 'dom-helpers';
import offset from 'dom-helpers/offset';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Checkbox } from '../Checkbox';
import { DropDownButton } from './ButtonSelectDropdown';

interface IPropsOp {
  options: IFilterOption<string | number>[];
  onToggleOption: (e: React.SyntheticEvent, id: string | number) => void;
  selected: string[] | number[];
  id: string;
  title?: string;
}

export const MultiSelectOptions = ({
  id,
  onToggleOption,
  options,
  selected,
  title,
}: IPropsOp) => {
  return (
    <div className="grid-list">
      {title.length > 0 && (
        <div className="item item--odd is-full mr-b-small mr-t-small">
          <span
            style={{ marginLeft: 0, fontSize: 12 }}
            className="has-text-grey-darker has-text-weight-bold is-uppercase"
          >
            {title}
          </span>
        </div>
      )}
      {options.map((option, key) => {
        return (
          <div
            key={key}
            className={cls(
              'item',
              `item--${key % 2 === 0 ? 'even' : 'odd'}`,
              option.isLabel && 'is-full mr-b-small mr-t-small',
            )}
          >
            {!option.isLabel ? (
              <Checkbox
                checkBoxId={id}
                onToggle={e => onToggleOption(e, option.id)}
                isCheck={selected.findIndex(id => option.id === id) > -1}
                {...option}
              />
            ) : (
              <span
                style={option.isLabel ? { marginLeft: 0, fontSize: 12 } : {}}
                className="has-text-grey-darker has-text-weight-bold is-uppercase"
              >
                {option.label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

type ExtraButton = {
  onClick: () => void;
  label: React.ReactNode | null;
  className?: string;
  style?: React.CSSProperties;
  hide?: boolean;
};

interface IProps {
  // required props
  id: string;
  name: string;
  isActiveDropdown: boolean;
  parentRef?: React.RefObject<HTMLElement>;

  options: IFilterOption<string | number>[];
  selectedOptions: string[] | number[];

  // events
  onSave: (ids: string[] | number[]) => void;
  onClose: () => void;
  extraButtons?: ExtraButton[];
  controlMobile?: boolean;
  multi?: boolean;
  headerName?: string;
  hideLabel?: boolean;
  // onUnSelect: (e: React.SyntheticEvent) => void,
  // onSelectOption: (id: string | number) => void
}

const mobileStyle: Record<string, CSSProperties> = {
  dropdownMenu: {
    position: 'fixed',
    top: '0',
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 1,
  },
  boxContainer: {
    position: 'absolute',
    marginTop: 0,
    paddingBottom: '70px',
    paddingTop: '10px',
    left: 0,
    right: 0,
    top: '65px',
    bottom: 0,
    paddingLeft: '1rem',
    paddingRight: '1rem',
    overflowY: 'scroll',
    scrollBehavior: 'smooth',
  },
  box: {
    position: 'relative',
    // top: '50%',
    // transform: 'translate(0, -50%)',
  },
};

export const MultiSelectBoxComponent: React.FC<IProps> = ({
  name,
  onClose,
  options,
  isActiveDropdown,
  id,
  selectedOptions,
  parentRef,
  onSave,
  extraButtons,
  controlMobile,
  multi = true,
  headerName,
  hideLabel = false,
}) => {
  const trans = useTrans();
  const deviceType = useSelector(selectDeviceType);

  const ref = useRef<HTMLDivElement>(null);
  const refBox = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({
    top: '',
    width: '',
    left: '',
    opacity: isActiveDropdown ? 1 : 0,
  });
  const [popUpFilterEl, setPopUpFilterEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const find = qsa(document.body, '#pop-product-filters .pop-page-content');
    if (find.length > 0) {
      setPopUpFilterEl(find[0]);
    }
  }, []);

  const findDropdownSize = (): CSSProperties => {
    // calculation is not need in mobile
    if (!parentRef.current || !ref.current) {
      return {};
    }

    const parentOffset = offset(parentRef.current);
    const childOffset = offset(ref.current);
    // const findCheckList = qsa(ref.current, '.is-checklist')
    const findBox = qsa(ref.current, '.box')?.[0];
    const checkListOffset = offset(findBox);

    // calculate if we need to add more space in top
    const navHeight = 65;
    let boxTop = checkListOffset.top - checkListOffset.height;
    if (popUpFilterEl) {
      const find = qsa(document.body, '#pop-product-filters .pop-page-outer');
      boxTop += find.length > 0 ? scrollTop(find[0]) : 0;
    }

    if (!popUpFilterEl || !controlMobile) {
    } else if (findBox) {
      // center box
      addStyle(findBox, {
        top: '50%',
        marginTop: `-${checkListOffset.height / 2}px`,
      });
    } else if (boxTop === 0) {
      // reset extra space in top
      addStyle(popUpFilterEl, {
        paddingTop: '',
      });
    } else if (navHeight > boxTop) {
      // const padTop = boxTop > 0 ? boxTop : ~--boxTop
      // addStyle(popUpFilterEl, {
      //   paddingTop: `${padTop + navHeight}px`,
      // })
    }

    return {
      width: parentOffset.width + 'px',
      left: -(childOffset.left - parentOffset.left) + 'px',
      top:
        deviceType === 'mobile'
          ? `-${checkListOffset.height + childOffset.height}px`
          : '',
      opacity: isActiveDropdown ? 1 : 0,
    };
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    let size = findDropdownSize();
    if (!size) {
      size = {
        top: '',
        width: '',
        left: '',
        ...dropdownStyle,
        opacity: 0,
      };
    }

    if (isActiveDropdown) {
      size.opacity = 1;
    }

    setDropdownStyle(size);

    // eslint-disable-next-line
  }, [deviceType, isActiveDropdown]);

  const [selected, setSelected] = useState(selectedOptions);
  // we need to save when dropdown will close & device is not mobile or
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // if (!isActiveDropdown && deviceType !== 'mobile') {
    //   onSave(selected)
    // }
    if (!isActiveDropdown) {
      onSave(selected);
    }
  }, [deviceType, isActiveDropdown]);

  // reset selected option when default is update
  useEffect(() => {
    if (selectedOptions.length === 0) {
      setSelected([]);
    }
    setSelected(selectedOptions);
  }, [selectedOptions]);

  const onToggleOption = (
    _e: React.SyntheticEvent,
    toggleId: string | number,
  ) => {
    let options: any[] = [];

    if (!multi) {
      if (options.length > 0 && options[0] === toggleId) {
        options = [];
      } else {
        options = [toggleId];
      }
    } else {
      options = [...selected];

      const indexToRemove = options.findIndex(id => id === toggleId);
      if (indexToRemove > -1) {
        options = remove(options, indexToRemove);
      } else {
        options.push(toggleId);
      }
    }

    setSelected(options);
    onSave(options);
  };

  const handleSaveButton = e => {
    e.preventDefault();
    onSave(selected);
    if (onClose) {
      onClose();
    }
  };

  const onUnSelectAll = e => {
    e.preventDefault();
    setSelected([]);
    onSave([]);
  };

  return (
    <>
      <DropDownButton
        className={classNames('dropdown-ch')}
        label={name}
        forwardRef={ref}
        isActive={isActiveDropdown}
        hideLabel={hideLabel}
      >
        <div
          className={classNames(
            'dropdown-menu is-checklist',
            !isActiveDropdown && 'hide',
          )}
          style={
            deviceType === 'mobile' ? mobileStyle.dropdownMenu : dropdownStyle
          }
        >
          <div
            className="dropdown-box"
            ref={refBox}
            style={deviceType === 'mobile' ? mobileStyle.boxContainer : {}}
          >
            <div
              className="box"
              style={deviceType === 'mobile' ? mobileStyle.box : {}}
            >
              <div className="dropdown-header">
                <div className="eb eb-close" onClick={onClose} />
                <div className="dropdown-header__title">
                  {name || headerName}
                </div>
              </div>
              <MultiSelectOptions
                id={id}
                title={''}
                selected={selected}
                onToggleOption={onToggleOption}
                options={options}
              />
              <div className="dropdown-footer">
                <div className="buttons">
                  <button className="button is-light" onClick={onUnSelectAll}>
                    {trans('filter_unselectall')}
                  </button>
                  <div className="is-m-full">
                    {extraButtons
                      ? extraButtons.map((item, pos) => {
                          if (item.hide) {
                            return null;
                          }

                          return (
                            <button
                              key={pos}
                              style={item?.style ?? {}}
                              className={`button ${item?.className ?? ''}`}
                              onClick={item.onClick}
                            >
                              {item.label}
                            </button>
                          );
                        })
                      : null}
                    <button
                      className="button is-danger"
                      onClick={handleSaveButton}
                    >
                      {trans('filter_save')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DropDownButton>
    </>
  );
};

export default MultiSelectBoxComponent;
